import type { ICellRendererComp, ICellRendererParams } from "ag-grid-enterprise";
import { showConfirmModal } from "../confirm_modal";

interface RowAction {
  type?: "badge" | "action";
  label: string;
  url?: string;
  icon?: string;
  method?: string;
  remote?: boolean;
  rel?: string;
  target?: string;
  confirm?: string;
  confirm_title?: string;
  confirm_danger?: string;
  turbo_frame?: string;
}

async function executeAction(action: RowAction): Promise<void> {
  const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
  await fetch(action.url ?? "#", {
    method: (action.method ?? "get").toUpperCase(),
    headers: {
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "text/html, application/xhtml+xml",
    },
    credentials: "same-origin",
  });
  window.location.reload();
}

export class ActionsCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: ICellRendererParams): void {
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-actions-cell";

    if (params.node.group) return;

    const rowActions = (params.data as Record<string, unknown>)?.row_actions;
    if (!Array.isArray(rowActions) || rowActions.length === 0) return;

    (rowActions as RowAction[]).forEach((action) => {
      if (action.type === "badge") {
        const badge = document.createElement("span");
        badge.className = "ag-actions-cell__badge";
        badge.textContent = action.label;
        this.eGui.appendChild(badge);
      } else {
        const link = document.createElement("a");
        link.className = "toolkit-button-component toolkit-button-component--toolbar-chip";
        if (action.icon) {
          link.classList.add("toolkit-button-component--with-icon");
        }
        link.href = action.url || "#";

        if (action.rel) {
          link.rel = action.rel;
        }

        // Turbo Drive skips links with a target. Without one it intercepts the click, gets back the
        // `text/csv` a download redirects to, cannot render it, and the click silently does nothing.
        if (action.target) {
          link.target = action.target;
        }

        if (action.turbo_frame) {
          const frameId = action.turbo_frame;
          const href = action.url || "#";
          link.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const frame = document.querySelector<HTMLElement>(`turbo-frame#${frameId}`);
            if (frame) frame.setAttribute("src", href);
          });
        } else if (action.confirm || action.confirm_title || action.confirm_danger) {
          link.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const confirmed = await showConfirmModal({
              title: action.confirm_title,
              danger: action.confirm_danger,
              message: action.confirm,
              confirmLabel: action.label,
            });
            if (confirmed) await executeAction(action);
          });
        } else if (action.method && action.method !== "get") {
          link.dataset.method = action.method;
          // Same opt-out AgGridBaseController's selection menu honours: an endpoint that answers
          // with a redirect has to navigate, or an XHR follows it invisibly and nothing happens.
          if (action.remote !== false) link.dataset.remote = "true";
        }

        if (action.icon) {
          const icon = document.createElement("i");
          icon.className = `fas fa-${action.icon}`;
          icon.setAttribute("aria-hidden", "true");
          link.appendChild(icon);
        }

        const label = document.createElement("span");
        label.textContent = action.label;
        link.appendChild(label);

        this.eGui.appendChild(link);
      }
    });
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }
}
